<template>
  <div id="product" class="product-view">
    <!-- 内容区域 -->
    <div class="product-main">
      <div class="product-top">
        <div class="product-header">
          <h1 class="product-title">{{ t("index.产品中心") }}</h1>
          <div class="view-all-btn" @click="viewAll">
            <span>{{ t("index.查看全部") }}</span>
            <span>></span>
          </div>
        </div>

        <div class="product-category product-category-2">
          <div
            v-for="(item, index) in childTypeList"
            :key="item.id"
            :class="['category-item', secondaryIndex === index ? 'active' : '']"
            @click="checkSecondType(index)"
          >
            <span>{{ isEnglish ? item.nameEn : item.name }}</span>
          </div>
        </div>
      </div>

      <div class="produce-show">
        <div class="show-container">
          <div class="swiper-nav swiper-nav-left" @click="prevSlide">
            <img alt="Previous" src="@/assets/image/left.png" />
          </div>

          <div class="show-swiper">
            <Swiper
              :autoplay="{
                delay: 1500,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
              }"
              :breakpoints="breakpoints"
              :loop="true"
              :modules="modules"
              class="mySwiper"
              @swiper="setSwiperRef"
            >
              <SwiperSlide v-for="(product, index) in productList" :key="product.id" @click="showModal(product)">
                <div class="product-card">
                  <div class="image-container">
                    <el-image
                      :alt="`${product.name}`"
                      :src="product.miniPicture[0] || 'https://picsum.photos/200/200?random=' + index"
                      class="main-image"
                      fit="contain"
                      hide-on-click-modal
                    />
                  </div>
                  <div class="product-info">
                    <p>{{ isEnglish ? product.nameEn : product.name }}</p>
                    <p class="product-desc" v-html="isEnglish ? product.textEn : product.text"></p>
                  </div>
                </div>
              </SwiperSlide>
            </Swiper>
          </div>

          <div class="swiper-nav swiper-nav-right" @click="nextSlide">
            <img alt="Next" src="@/assets/image/right.png" />
          </div>
        </div>
      </div>
    </div>

    <!-- 弹窗 -->
    <div v-if="modalStatus" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <Swiper :breakpoints="breakpoints" :modules="modules" class="swiper-modal" @swiper="setSwiperRef2">
          <SwiperSlide v-for="(item, index) in carouselList" :key="index">
            <el-image
              :alt="item.name"
              :src="item.image"
              :preview-src-list="carouselList.map((i) => i.image)"
              :initial-index="index"
              preview-teleported
              class="carousel-image"
              fit="contain"
            />
          </SwiperSlide>
        </Swiper>

        <div class="modal-footer">
          <button class="arrow-btn" @click="prevSlide2">
            <img :alt="t('index.上一张')" src="@/assets/image/left.png" />
          </button>
          <div class="carousel-info">
            <span class="carousel-name">{{ currentName }}</span>
            <p class="carousel-desc" v-html="isEnglish ? selectedProduct.textEn : selectedProduct.text"></p>
          </div>
          <button class="arrow-btn" @click="nextSlide2">
            <img :alt="t('index.下一张')" src="@/assets/image/right.png" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue"
import { Autoplay, FreeMode } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/vue"
import { getCommodityById, getType } from "@/api/index"
import { ElMessage } from "element-plus"
import i18n from "@/locale"
import { useI18n } from "vue-i18n"
import { useRouter } from "vue-router"

import "swiper/css"
import "swiper/css/free-mode"

const { t } = useI18n()
const router = useRouter()
const modules = [FreeMode, Autoplay]

const isEnglish = computed(() => i18n.global.locale.value === "en")
const childTypeList = ref([])
const productList = ref([])
const secondaryIndex = ref(0)
const swiperRef = ref(null)
const swiperRef2 = ref(null)
const breakpoints = {
  640: {
    slidesPerView: 1,
    spaceBetween: 10,
  },
  768: {
    slidesPerView: 3,
    spaceBetween: 10,
  },
  1200: {
    slidesPerView: 5,
    spaceBetween: 15,
  },
  1600: {
    slidesPerView: 7,
    spaceBetween: 15,
  },
}
const selectedProduct = ref(null)
const modalStatus = ref(false)

const carouselList = computed(() => {
  if (!selectedProduct.value) return []
  const pictures = selectedProduct.value.picture || []
  const name = isEnglish.value ? selectedProduct.value.nameEn : selectedProduct.value.name
  return pictures.map((url) => ({ image: url, name: name || "" }))
})

const currentName = computed(() => {
  if (!swiperRef2.value || !carouselList.value.length) return ""
  const idx = swiperRef2.value.activeIndex
  return carouselList.value[idx]?.name || ""
})

const setSwiperRef = (swiper) => {
  swiperRef.value = swiper
}

const setSwiperRef2 = (swiper) => {
  swiperRef2.value = swiper
}

const prevSlide = () => {
  if (swiperRef.value) {
    swiperRef.value.slidePrev()
  }
}

const nextSlide = () => {
  if (swiperRef.value) {
    swiperRef.value.slideNext()
  }
}

const getTypeList = () => {
  getType().then((res) => {
    if (res.code !== 200) return ElMessage.error((isEnglish.value ? res.msgEn : res.msg) || i18n.global.t("message.数据获取失败"))
    childTypeList.value = res.typeInfoList || []
    if (childTypeList.value.length > 0) {
      getProductsByType(childTypeList.value[0].id)
    }
  })
}

const getProductsByType = (typeId) => {
  getCommodityById({ typeId }).then((res) => {
    if (res.code !== 200) return ElMessage.error((isEnglish.value ? res.msgEn : res.msg) || i18n.global.t("message.数据获取失败"))
    const list = res.data || res.commodityList || []
    productList.value = list.map((item) => ({
      ...item,
      picture: typeof item.picture === "string" ? item.picture.split(",").filter(Boolean) : item.picture || [],
      miniPicture: typeof item.miniPicture === "string" ? item.miniPicture.split(",").filter(Boolean) : item.miniPicture || [],
    }))
  })
}

const checkSecondType = (index) => {
  secondaryIndex.value = index
  getProductsByType(childTypeList.value[index].id)
  if (swiperRef.value) {
    swiperRef.value.slideTo(0, 300)
  }
}

const viewAll = () => {
  router.push("/goods?index=0")
}

const prevSlide2 = () => {
  if (swiperRef2.value) {
    if (swiperRef2.value.isBeginning) {
      ElMessage({
        message: t("message.没有更多产品了"),
        type: "info",
        duration: 2000,
      })
    } else {
      swiperRef2.value.slidePrev()
    }
  }
}

const nextSlide2 = () => {
  if (swiperRef2.value) {
    if (swiperRef2.value.isEnd) {
      ElMessage({
        message: t("message.没有更多产品了"),
        type: "info",
        duration: 2000,
      })
    } else {
      swiperRef2.value.slideNext()
    }
  }
}

const closeModal = () => {
  modalStatus.value = false
}

const showModal = (product) => {
  selectedProduct.value = product
  modalStatus.value = true
}

watch(
  productList,
  () => {
    if (swiperRef.value) {
      swiperRef.value.update()
    }
  },
  { deep: true }
)

onMounted(() => {
  getTypeList()
})
</script>

<style lang="scss" scoped>
.product-view {
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #fff;
  overflow: hidden;

  .product-main {
    width: 100%;
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 20px 0;
    box-sizing: border-box;
    justify-content: space-around;

    .product-top {
      width: 80%;
      display: flex;
      flex-direction: column;
      gap: 30px;

      .product-header {
        width: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;

        .product-title {
          font-size: 32px;
          font-weight: bold;
          color: #000;
          margin: 0;
        }

        .view-all-btn {
          padding: 10px 20px;
          background: transparent;
          border-radius: 24px;
          border: 1px solid #333;
          color: #333;
          font-weight: bold;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s;

          &:hover {
            border-color: var(--new-btnColor);
            background: var(--new-btnColor);
            color: #fff;
          }
        }
      }

      .product-category-2 {
        width: 100%;
        display: flex;
        justify-content: flex-start;
        gap: 15px;

        .category-item {
          padding: 8px 20px;
          border: 1px solid #000;
          border-radius: 24px;
          cursor: pointer;
          transition: all 0.3s;

          span {
            font-weight: bold;
            color: #000;
          }

          &:hover {
            background: #000;
            border-color: #000;

            span {
              color: #fff;
            }
          }
        }

        .active {
          background: #000;
          border-color: #000;

          span {
            color: #fff;
          }
        }
      }
    }

    .produce-show {
      width: 90%;
      display: flex;
      align-items: center;

      .show-container {
        width: 100%;
        display: flex;
        align-items: center;
        gap: 20px;

        .swiper-nav {
          width: 40px;
          height: 40px;
          cursor: pointer;
          flex-shrink: 0;

          img {
            width: 100%;
            height: 100%;
            object-fit: contain;
          }
        }

        .show-swiper {
          flex: 1;
          height: 100%;
          overflow: hidden;
          max-height: 400px;

          .mySwiper {
            width: 100%;
            height: 100%;

            .product-card {
              width: 200px;
              height: 100%;
              display: flex;
              flex-direction: column;
              align-items: center;
              gap: 20px;

              .image-container {
                width: 180px;
                height: 180px;
                display: flex;
                align-items: center;
                justify-content: center;
                overflow: hidden;
                border: 1px solid #eee;
                border-radius: 8px;

                .main-image {
                  max-width: 100%;
                  max-height: 100%;
                  object-fit: contain;
                }
              }

              .product-info {
                width: 100%;
                text-align: center;
                font-size: 14px;
                color: #333;

                p {
                  margin: 5px 0;
                }

                .product-desc {
                  font-size: 12px;
                  color: #999;
                  line-height: 1.8;
                  white-space: pre-line;
                }
              }
            }
          }
        }
      }
    }
  }

  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;

    .modal-content {
      width: 90%;
      height: 65%;
      padding: 20px;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      gap: 20px;
      margin: auto;
      background: rgba(255, 255, 255, 0.5);
      backdrop-filter: blur(2px) saturate(180%);
      box-shadow: 0 8px 32px rgba(31, 38, 135, 0.2), inset 0 4px 20px rgba(255, 255, 255, 0.3);
      position: relative;
      border: 1px solid rgba(255, 255, 255, 0.8);
      border-radius: 2rem;

      &::after {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(255, 255, 255, 0.1);
        border-radius: 2rem;
        backdrop-filter: blur(1px);
        box-shadow: inset -10px -8px 0px -11px rgba(255, 255, 255, 1), inset 0px -9px 0px -8px rgba(255, 255, 255, 1);
        opacity: 0.6;
        z-index: -1;
        filter: blur(1px) drop-shadow(10px 4px 6px black) brightness(115%);
      }

      .swiper-modal {
        width: 100%;
        flex: 1;
        overflow: hidden;

        .carousel-image {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
      }

      .modal-footer {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: space-around;

        .arrow-btn {
          background: none;
          border: none;
          cursor: pointer;
          padding: 0;
          transition: transform 0.2s ease;

          img {
            width: 45px;
            height: 45px;
          }

          &:hover {
            transform: scale(1.1);
          }

          &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
            transform: none;
          }
        }

        .carousel-info {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          width: 70%;

          .carousel-name {
            font-size: 24px;
            font-weight: 600;
            color: #333;
            text-align: center;
          }

          .carousel-desc {
            margin: 0;
            font-size: 14px;
            color: #333;
            text-align: center;
            line-height: 1.8;
            white-space: pre-line;
          }
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .product-view {
    height: auto;

    .product-main {
      gap: 40px;
      .product-header {
        width: 90%;

        .product-title {
          font-size: 24px;
        }

        .view-all-btn {
          padding: 8px 16px;
          font-size: 14px;
        }
      }

      .product-category-2 {
        width: 90%;
        flex-wrap: wrap;
        gap: 10px;

        .category-item {
          padding: 6px 16px;
          font-size: 14px;
        }
      }

      .produce-show {
        width: 95%;

        .show-container {
          .swiper-nav {
            width: 30px;
            height: 30px;
          }

          .show-swiper {
            max-height: none;

            .mySwiper {
              .product-card {
                width: 150px;
                margin: 0 auto;

                .image-container {
                  width: 130px;
                  height: 130px;
                }

                .product-info {
                  font-size: 12px;
                }
              }
            }
          }
        }
      }
    }
  }
  .modal-overlay {
    .modal-content {
      width: 95%;
      height: auto;
      min-height: 280px;
      padding: 15px;
      border-radius: 1.2rem;

      &::after {
        border-radius: 1.2rem;
      }

      .modal-footer {
        .arrow-btn img {
          width: 35px;
          height: 35px;
        }

        .carousel-info {
          width: auto;

          .carousel-name {
            font-size: 16px;
          }
        }
      }
    }
  }

  @media (max-width: 480px) {
    .modal-overlay {
      .modal-content {
        padding: 10px;

        .modal-footer {
          .arrow-btn img {
            width: 45px;
            height: 45px;
          }

          .carousel-info {
            width: 70%;

            .carousel-name {
              font-size: 14px;
            }

            .carousel-desc {
              font-size: 12px;
            }
          }
        }
      }
    }
  }
}
</style>

<style>
.el-image-viewer__wrapper {
  z-index: 10000 !important;
}
.el-message {
  z-index: 10001 !important;
}
</style>
